/**
 * "Active Learning Paths" — the first of the dashboard's three summary panels.
 *
 * ------------------------------------------------------------------ the prototype ----
 * The prototype's dashboard has TWO pathway treatments, not one, and this file is the first
 * of them:
 *
 *   1. `#screen-dashboard` → `.dash__grid` (index.html:5125-5162) is a three-column row:
 *      **Active Learning Paths** | Upcoming deadlines | Recent certificates. The paths panel
 *      is `.card.lp-summary` (:5127-5137) and each row (`.lp-summary__item`) carries the
 *      path's name, one meta line — "In progress · 2 of 5 courses · 40% complete" — a 3px
 *      progress bar, and a Continue button. Its head has an "All →" link, which the
 *      prototype wires to the paths catalogue filtered to the ones you are in (:10988).
 *   2. `.dash__library.mylearning` below it, where paths and courses sit in ONE grid as one
 *      card shape. That is `MyLearning.tsx`.
 *
 * The two are not redundant: this panel is the "what is asking something of me right now"
 * summary beside the deadlines, and the library is the whole shelf.
 *
 * NO DATE ON THIS PANEL, and that is checked rather than assumed. The prototype's rows carry
 * no due date, and the backend could not fill one if they did: `LearningPath` has no deadline,
 * end date or expiry field of any kind (models.py — key, uuid, display_name, subtitle,
 * description, image, level, duration, time_commitment, sequential, invite_only). The only
 * date-shaped thing in the plugin is `LearningPathStep.course_dates`, a property that returns
 * the STEP'S COURSE's own (start, end) — a course date, not a pathway date. So this panel
 * states progress, which is real, and no date, because there is not one to state.
 *
 * ------------------------------------------------------------------- "active" -------
 * ACTIVE MEANS AN ACTIVE ENROLMENT, not "published and visible". The list endpoint annotates
 * each path with `enrollment_date` from `LearningPathEnrollment` filtered on
 * `is_active=True` (models.py:53-56), and `assembleView` turns a null there into
 * `state: 'not_enrolled'`. So `state !== 'not_enrolled'` IS `is_active`, and a learner who
 * was removed from a path stops seeing it here even though the row still exists.
 *
 * On an academy where nobody has been enrolled in a path yet this panel renders NOTHING —
 * no heading, no empty box, no spinner. That is the same rule the two panels beside it
 * follow, and it is the right one: a dashboard that grows a permanent empty "Active Learning
 * Paths" card is a standing apology for a feature nobody here uses yet.
 */

import { Link } from 'react-router-dom';
import { Banner, Button, Icon } from '../../shared/ui';
import { text } from '../../shared/lib/config';
import { pathHref, pct, type LearningPathView } from '../../shared/lib/paths';
import './dashboard-paths.css';

export default function ActivePathsPanel({
  paths,
  pending,
  failed,
  onRetry,
}: {
  paths: LearningPathView[];
  pending: boolean;
  failed: boolean;
  onRetry: () => void;
}) {
  // Nothing to say yet. Rendering a heading and removing it a moment later is worse than
  // arriving late: the row would reflow under a reader who had started on it.
  if (pending) return null;

  // One inline alert for one failed widget, which is the page spec's stated error rule —
  // the rest of the dashboard has already rendered and is still true.
  if (failed) {
    return (
      <div className="card dash__panel">
        <div className="section-head">
          <h3>{activeHeading()}</h3>
        </div>
        <Banner tone="danger">
          <strong>These could not be loaded.</strong> Nothing has changed, so retrying is safe.
          <div className="cat__banner-action">
            <Button variant="secondary" onClick={onRetry}>
              Try again
            </Button>
          </div>
        </Banner>
      </div>
    );
  }

  const mine = paths.filter(isActive).sort(byAttention);
  if (mine.length === 0) return null;

  return (
    <div className="card dash__panel">
      <div className="section-head">
        <h3>{activeHeading()}</h3>
        <Link to="/paths" className="dash__panel-all">
          All <Icon name="arrow_forward" />
        </Link>
      </div>

      <ul className="dash__panel-list lp-summary">
        {mine.map((view) => (
          <li className="lp-summary__item" key={view.path.key}>
            <div className="lp-summary__info">
              <span className="lp-summary__name" title={view.path.title}>
                {view.path.title}
              </span>
              <span className="lp-summary__meta">{summarise(view)}</span>
              {/* Drawn only once there is something to draw. An empty 3px track under a
                  path somebody joined yesterday is a picture of falling behind. */}
              {view.progress > 0 && (
                <div className="lp-summary__progress">
                  <div
                    className="lp-summary__progress-bar"
                    data-done={view.state === 'completed' || undefined}
                    style={{ width: `${pct(view.progress)}%` }}
                  />
                </div>
              )}
            </div>
            <Link className="ui-btn" data-variant="secondary" to={pathHref(view.path.slug)}>
              {view.state === 'completed' ? 'View' : 'Continue'}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* -------------------------------------------------------------------- helpers ----- */

/** The panel's own heading, built from whatever this academy calls a path. */
function activeHeading(): string {
  return `Active ${text('nav_paths', 'Learning Paths')}`;
}

/** An ACTIVE path enrolment — see the note on `is_active` at the top of this file. */
const isActive = (view: LearningPathView) => view.state !== 'not_enrolled';

/**
 * The prototype's one meta line: state · courses done · percent.
 *
 * The percentage is dropped when it is zero rather than printed as "0% complete", for the
 * same reason the bar is: it is a real measurement and it is also a discouraging way to
 * greet somebody who has only just joined. "Not started · 0 of 2 courses" says the same
 * thing without the scolding.
 */
function summarise(view: LearningPathView): string {
  const label =
    view.state === 'completed' ? 'Completed' : view.state === 'in_progress' ? 'In progress' : 'Not started';

  const courses = `${view.stepsCompleted} of ${view.stepCount} ${view.stepCount === 1 ? 'course' : 'courses'}`;
  const percent = view.progress > 0 ? ` · ${pct(view.progress)}% complete` : '';

  return `${label} · ${courses}${percent}`;
}

/**
 * Unfinished work first.
 *
 * A completed path is an achievement and belongs here, but under the ones still asking
 * something — a finished path at the top of the panel is the dashboard telling somebody they
 * are done when they are not.
 */
const RANK: Record<LearningPathView['state'], number> = {
  in_progress: 0,
  enrolled: 1,
  completed: 2,
  not_enrolled: 3,
};

function byAttention(a: LearningPathView, b: LearningPathView): number {
  return RANK[a.state] - RANK[b.state] || a.path.title.localeCompare(b.path.title);
}
